import { FC, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { useLogout } from '@corratech/pylot-auth-manager'
import MenuClose from '@components/icons/MenuClose'
import s from './AccountPanel.module.scss'

export type AccountCustomer = {
    firstname?: string | null
    email?: string | null
}

interface Props {
    open: boolean
    onClose: () => void
    customer?: AccountCustomer | null
}

const Chevron = () => (
    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden>
        <path
            d="M0.75 0.75L5.75 5.75L0.75 10.75"
            stroke="#534E46"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const LogoutIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
            d="M4.16667 17.5C3.70833 17.5 3.31611 17.3369 2.99 17.0108C2.66389 16.6847 2.50056 16.2922 2.5 15.8333V4.16667C2.5 3.70833 2.66333 3.31611 2.99 2.99C3.31667 2.66389 3.70889 2.50056 4.16667 2.5H9.16667C9.40278 2.5 9.60083 2.58 9.76083 2.74C9.92083 2.9 10.0006 3.09778 10 3.33333C9.99944 3.56889 9.91944 3.76694 9.76 3.9275C9.60056 4.08806 9.40278 4.16778 9.16667 4.16667H4.16667V15.8333H9.16667C9.40278 15.8333 9.60083 15.9133 9.76083 16.0733C9.92083 16.2333 10.0006 16.4311 10 16.6667C9.99944 16.9022 9.91944 17.1003 9.76 17.2608C9.60056 17.4214 9.40278 17.5011 9.16667 17.5H4.16667ZM14.3125 10.8333H8.33333C8.09722 10.8333 7.89944 10.7533 7.74 10.5933C7.58056 10.4333 7.50056 10.2356 7.5 10C7.49944 9.76444 7.57944 9.56667 7.74 9.40667C7.90056 9.24667 8.09833 9.16667 8.33333 9.16667H14.3125L12.75 7.60417C12.5972 7.45139 12.5208 7.26389 12.5208 7.04167C12.5208 6.81944 12.5972 6.625 12.75 6.45833C12.9028 6.29167 13.0972 6.20472 13.3333 6.1975C13.5694 6.19028 13.7708 6.27028 13.9375 6.4375L16.9167 9.41667C17.0833 9.58333 17.1667 9.77778 17.1667 10C17.1667 10.2222 17.0833 10.4167 16.9167 10.5833L13.9375 13.5625C13.7708 13.7292 13.5731 13.8092 13.3442 13.8025C13.1153 13.7958 12.9172 13.7089 12.75 13.5417C12.5972 13.375 12.5244 13.1772 12.5317 12.9483C12.5389 12.7194 12.6186 12.5283 12.7708 12.375L14.3125 10.8333Z"
            fill="#D50032"
        />
    </svg>
)

const FavoritesIcon = () => (
    <svg width="20" height="19" viewBox="0 0 20 19" fill="none" aria-hidden>
        <path
            d="M18.5008 9.21407C17.102 13.1955 13.5763 16.278 9.86837 18.1178C9.69586 18.2035 9.3321 18.2029 9.15959 18.1178C5.41596 16.2576 1.81646 13.1064 0.473244 9.05518C-0.457416 6.24845 -0.0942313 3.08023 2.26987 1.1406C4.59766 -0.726394 7.77892 -0.215665 9.51426 2.21597C11.2235 -0.206586 14.5177 -0.75874 16.8023 1.13776C18.069 2.24037 18.8402 3.76518 18.9883 5.45058C19.1018 6.74215 18.9287 7.99684 18.5008 9.21407Z"
            fill="#534E46"
        />
    </svg>
)

const SavedIcon = () => (
    <svg width="15" height="19" viewBox="0 0 15 19" fill="none" aria-hidden>
        <path
            d="M13.125 0H1.875C0.84375 0 0 0.854786 0 1.89953V18.0455C0 18.3874 0.178125 18.7008 0.46875 18.8718C0.759375 19.0427 1.11563 19.0427 1.40625 18.8718L7.5 15.3387L13.5938 18.8718C13.7344 18.9573 13.9031 18.9953 14.0625 18.9953C14.2219 18.9953 14.3906 18.9573 14.5312 18.8718C14.8219 18.7008 15 18.3874 15 18.0455V1.89953C15 0.854786 14.1562 0 13.125 0Z"
            fill="#534E46"
        />
    </svg>
)

const GiftCardsIcon = () => (
    <svg width="18" height="17" viewBox="0 0 18 17" fill="none" aria-hidden>
        <path
            d="M11.0516 2.365C11.3128 1.92156 11.7872 1.65 12.2994 1.65H12.375C13.1347 1.65 13.75 2.26531 13.75 3.025C13.75 3.78469 13.1347 4.4 12.375 4.4H9.85531L11.0516 2.365ZM6.54844 2.365L7.74469 4.4H5.225C4.46531 4.4 3.85 3.78469 3.85 3.025C3.85 2.26531 4.46531 1.65 5.225 1.65H5.30062C5.81281 1.65 6.29063 1.92156 6.54844 2.365ZM9.62844 1.52969L8.8 2.93906L7.97156 1.52969C7.41469 0.580938 6.39719 0 5.30062 0H5.225C3.55438 0 2.2 1.35438 2.2 3.025C2.2 3.52 2.32031 3.9875 2.53 4.4H1.1C0.491562 4.4 0 4.89156 0 5.5V6.6C0 7.20844 0.491562 7.7 1.1 7.7H16.5C17.1084 7.7 17.6 7.20844 17.6 6.6V5.5C17.6 4.89156 17.1084 4.4 16.5 4.4H15.07C15.2797 3.9875 15.4 3.52 15.4 3.025C15.4 1.35438 14.0456 0 12.375 0H12.2994C11.2028 0 10.1853 0.580938 9.62844 1.52625V1.52969ZM16.5 9.35H9.625V16.5H14.3C15.5134 16.5 16.5 15.5134 16.5 14.3V9.35ZM7.975 9.35H1.1V14.3C1.1 15.5134 2.08656 16.5 3.3 16.5H7.975V9.35Z"
            fill="#534E46"
        />
    </svg>
)

const SubscriptionsIcon = () => (
    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden>
        <path
            d="M13 3H4C2.9 3 2 3.9 2 5V10H4V5H13V8L18 4L13 0V3ZM14 15H5V12L0 16L5 20V17H14C15.1 17 16 16.1 16 15V10H14V15Z"
            fill="#534E46"
        />
    </svg>
)

const Avatar = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden>
        <rect width="48" height="48" rx="24" fill="#F7F3EE" />
        <g clipPath="url(#account-avatar-clip)">
            <path
                d="M29.7118 25.9843C29.1291 25.167 28.3388 24.4912 27.4447 24.0118C26.9897 23.7682 26.5107 23.5796 26.0077 23.4617C25.6804 23.3831 28.0274 23.1945 28.8736 20.2554C29.9673 16.4283 26.0237 12.7426 22.2158 14.4165C20.3638 15.2259 19.0785 17.1591 19.1264 19.1473C19.1584 20.5619 19.789 22.3458 22.0881 23.1945C19.8768 23.5167 18.5676 24.7662 17.6815 26.5894C16.4202 29.1827 16.4521 32.5462 16.524 32.6405C16.5878 32.7269 25.1296 35.8625 31.3963 32.3026C31.7076 32.1297 31.3643 28.2947 29.7118 25.9843Z"
                fill="#534E46"
            />
        </g>
        <defs>
            <clipPath id="account-avatar-clip">
                <rect
                    width="15"
                    height="20"
                    fill="white"
                    transform="translate(16.5 14)"
                />
            </clipPath>
        </defs>
    </svg>
)

const AccountPanel: FC<Props> = ({ open, onClose, customer }) => {
    const { t } = useTranslation('common')
    const { logout } = useLogout()
    const firstName = customer?.firstname?.trim()
    const email = customer?.email?.trim()

    useEffect(() => {
        if (!open) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    if (!open || typeof document === 'undefined') return null

    const links = [
        { name: t('My Account'), href: '/account' },
        { name: t('My Orders'), href: '/account/orders' },
        {
            name: t('Customer Program'),
            href: 'https://clientes.casaideas.cl/',
            external: true
        }
    ]

    const shortcuts = [
        {
            name: t('account|My favorites', { defaultValue: 'Mis favoritos' }),
            href: '/account/wishlist',
            icon: <FavoritesIcon />
        },
        {
            name: t('Saved', { defaultValue: 'Guardados' }),
            href: '/account/saved-cards',
            icon: <SavedIcon />
        },
        {
            name: t('Gift Cards', { defaultValue: 'Gift Cards' }),
            href: '/account/gift-card',
            icon: <GiftCardsIcon />
        },
        {
            name: t('account|Newsletter Subscriptions', {
                defaultValue: 'Mis suscripciones'
            }),
            href: '/account/newsletter',
            icon: <SubscriptionsIcon />
        }
    ]

    return createPortal(
        <div className={s.root}>
            <button
                type="button"
                className={s.overlay}
                aria-label={t('Close panel')}
                onClick={onClose}
            />
            <div
                className={s.panel}
                role="dialog"
                aria-modal="true"
                aria-labelledby="account-panel-title"
            >
                <header className={s.header}>
                    <h2 id="account-panel-title" className={s.title}>
                        {t('My Account')}
                    </h2>
                    <button
                        type="button"
                        className={s.close}
                        aria-label={t('Close panel')}
                        onClick={onClose}
                    >
                        <MenuClose />
                    </button>
                </header>
                <div className={s.body}>
                <div className={s.profile}>
                    <Avatar />
                    <div className={s.profileText}>
                        <p className={s.hello}>
                            {firstName
                                ? `¡${t('Hello')}, ${firstName}!`
                                : `¡${t('Hello')}!`}
                        </p>
                        {email ? <p className={s.email}>{email}</p> : null}
                    </div>
                </div>
                <ul className={s.links}>
                    {links.map((link) => (
                        <li key={link.href}>
                            <Link
                                href={link.href}
                                className={s.link}
                                target={link.external ? '_blank' : undefined}
                                rel={link.external ? 'noreferrer' : undefined}
                                onClick={onClose}
                            >
                                <span>{link.name}</span>
                                <Chevron />
                            </Link>
                        </li>
                    ))}
                </ul>
                <p className={s.quickTitle}>
                    {t('Quick access', { defaultValue: 'Accesos rápidos' })}
                </p>
                <div className={s.shortcuts}>
                    {shortcuts.map((item) => (
                        <Link
                            key={item.name}
                            href={item.href}
                            className={s.card}
                            onClick={onClose}
                        >
                            <span className={s.cardIcon}>{item.icon}</span>
                            <span>{item.name}</span>
                        </Link>
                    ))}
                </div>
                <button
                    type="button"
                    className={s.logout}
                    onClick={() => {
                        onClose()
                        logout()
                    }}
                >
                    <LogoutIcon />
                    <span>{t('auth|Logout')}</span>
                </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default AccountPanel
